import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import { ProjectNameInput } from './ProjectNameInput';
import { LocationPicker } from './LocationPicker';
import { useProjectCreation } from '../../hooks/useProjectCreation';
import { slugify } from '../../utils/format';

interface NewProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [projectName, setProjectName] = useState('My Awesome Game');
  const [location, setLocation] = useState('');

  const { createProject, isCreating, error, clearError, setParentHandle, parentHandle } = useProjectCreation();

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      clearError();
    }
  }, [isOpen, clearError]);

  // Generate slug from project name
  const projectSlug = useMemo(() => slugify(projectName), [projectName]);

  // Full path preview
  const fullPath = useMemo(() => {
    if (!location || !projectSlug) return '';
    return `${location}/${projectSlug}`;
  }, [location, projectSlug]);

  // Validation
  const validation = useMemo(() => {
    const errors: Record<string, string> = {};

    if (!projectName.trim()) {
      errors.projectName = 'Project name is required';
    } else if (projectName.length > 50) {
      errors.projectName = 'Project name must be 50 characters or less';
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(projectName)) {
      errors.projectName = 'Only letters, numbers, spaces, hyphens, and underscores allowed';
    }

    if (!location || !parentHandle) {
      errors.location = 'Location is required - please select a folder';
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }, [projectName, location, parentHandle]);

  // Handle location change from LocationPicker (now includes handle)
  const handleLocationChange = useCallback((path: string, handle?: FileSystemDirectoryHandle) => {
    setLocation(path);
    if (handle) {
      setParentHandle(handle);
    }
  }, [setParentHandle]);

  const handleCreate = useCallback(async () => {
    if (!validation.isValid) return;

    try {
      await createProject({
        name: projectName,
        path: fullPath,
        template: 'empty',
        scriptLanguage: 'typescript',
        resolution: { width: 720, height: 1560 },
        gitInit: false,
        openAfterCreate: true,
      });
    } catch {
      // Error is already handled in the hook
    }
  }, [
    validation.isValid,
    projectName,
    fullPath,
    createProject,
  ]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="lg">
      <div className="flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <h2 className="text-xl font-semibold text-white">Create New Project</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X size={20} className="text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Project Name */}
          <ProjectNameInput
            value={projectName}
            onChange={setProjectName}
            error={validation.errors.projectName}
          />

          {/* Location */}
          <LocationPicker
            value={location}
            onChange={handleLocationChange}
            error={validation.errors.location}
            disabled={isCreating}
          />

          {/* Full Path Preview */}
          {fullPath && (
            <div className="text-sm">
              <span className="text-gray-500">Project will be created at: </span>
              <span className="font-mono text-gray-400 break-all">{fullPath}</span>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700 shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!validation.isValid || isCreating}
            loading={isCreating}
          >
            Create Project
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
